import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SlidersHorizontal, X, ChevronDown } from 'lucide-react';
import { productsAPI } from '../api/products';
import { Product, Category } from '../types';
import ProductCard from '../components/ui/ProductCard';
import { ProductGridSkeleton } from '../components/ui/SkeletonLoader';

const ShopPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const page = parseInt(searchParams.get('page') || '1');
  const category = searchParams.get('category') || '';
  const search = searchParams.get('search') || '';
  const sort = searchParams.get('sort') || '';
  const minPrice = searchParams.get('min_price') || '';
  const maxPrice = searchParams.get('max_price') || '';
  const sale = searchParams.get('sale') || '';

  const fetchProducts = useCallback(async () => {
    try {
      setIsLoading(true);
      const params: any = { page, limit: 12 };
      if (category) params.category = category;
      if (search) params.search = search;
      if (minPrice) params.min_price = parseFloat(minPrice);
      if (maxPrice) params.max_price = parseFloat(maxPrice);
      if (sale === 'true') params.sale = true;

      if (sort === 'newest') {
        params.sort_by = 'created_at';
      } else if (sort === 'price_asc') {
        params.sort_by = 'price';
        params.sort_order = 'asc';
      } else if (sort === 'price_desc') {
        params.sort_by = 'price';
        params.sort_order = 'desc';
      }

      const res = await productsAPI.getAll(params);
      if (res.success && res.data) {
        setProducts(res.data.products || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.total_pages || 1);
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setIsLoading(false);
    }
  }, [page, category, search, sort, minPrice, maxPrice, sale]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    const fetchCategories = async () => {
      const res = await productsAPI.getCategories();
      if (res.success && res.data) setCategories(res.data);
    };
    fetchCategories();
  }, []);

  const updateFilter = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  const hasFilters = category || search || minPrice || maxPrice || sort || sale;

  return (
    <div className="min-h-screen">
      {/* Page Header */}
      <div className="bg-light py-16 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs tracking-widest uppercase text-nude mb-3">
            {search ? 'Search Results' : sale === 'true' ? 'Sale' : category ? 'Collection' : 'All Pieces'}
          </p>
          <h1 className="font-serif text-5xl text-primary">
            {search
              ? `"${search}"`
              : sale === 'true'
              ? 'Sale'
              : category
              ? category.charAt(0).toUpperCase() + category.slice(1)
              : 'Shop'}
          </h1>
          {total > 0 && (
            <p className="text-xs text-nude mt-4 tracking-wide">
              {total} {total === 1 ? 'piece' : 'pieces'}
            </p>
          )}
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Toolbar */}
        <div className="flex items-center justify-between mb-8 pb-4 border-b border-light">
          <button
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="flex items-center space-x-2 text-xs tracking-widest uppercase hover:text-nude transition-colors"
          >
            <SlidersHorizontal size={14} />
            <span>Filter</span>
            {hasFilters && (
              <span className="bg-primary text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">
                !
              </span>
            )}
          </button>

          <div className="flex items-center space-x-6">
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center space-x-1 text-xs tracking-widest uppercase text-nude hover:text-primary transition-colors"
              >
                <X size={12} />
                <span>Clear</span>
              </button>
            )}

            {/* Sort */}
            <div className="relative group">
              <button className="flex items-center space-x-2 text-xs tracking-widest uppercase hover:text-nude transition-colors">
                <span>Sort</span>
                <ChevronDown size={12} />
              </button>
              <div className="absolute right-0 top-6 bg-white border border-light shadow-lg w-40 z-10 hidden group-hover:block animate-slide-down">
                {[
                  { label: 'Newest', value: 'newest' },
                  { label: 'Price: Low to High', value: 'price_asc' },
                  { label: 'Price: High to Low', value: 'price_desc' },
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => updateFilter('sort', option.value)}
                    className={`block w-full text-left px-4 py-3 text-xs tracking-wide hover:bg-light transition-colors ${
                      sort === option.value ? 'text-primary font-medium' : 'text-secondary'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Filter Panel */}
        <AnimatePresence>
          {isFilterOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden mb-8"
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 py-6 border-b border-light">
                {/* Categories */}
                <div>
                  <h3 className="text-xs tracking-widest uppercase mb-4 text-primary">
                    Category
                  </h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => updateFilter('category', '')}
                      className={`block text-xs tracking-wide hover:text-primary transition-colors ${
                        !category ? 'text-primary font-medium' : 'text-nude'
                      }`}
                    >
                      All
                    </button>
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => updateFilter('category', cat.slug)}
                        className={`block text-xs tracking-wide hover:text-primary transition-colors ${
                          category === cat.slug ? 'text-primary font-medium' : 'text-nude'
                        }`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Price Range */}
                <div>
                  <h3 className="text-xs tracking-widest uppercase mb-4 text-primary">
                    Price
                  </h3>
                  <div className="space-y-3">
                    <input
                      type="number"
                      placeholder="Min"
                      value={minPrice}
                      onChange={(e) => updateFilter('min_price', e.target.value)}
                      className="input-luxury text-xs"
                    />
                    <input
                      type="number"
                      placeholder="Max"
                      value={maxPrice}
                      onChange={(e) => updateFilter('max_price', e.target.value)}
                      className="input-luxury text-xs"
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Products Grid */}
        {isLoading ? (
          <ProductGridSkeleton count={12} />
        ) : products.length > 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
          >
            {products.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </motion.div>
        ) : (
          <div className="text-center py-24">
            <p className="font-serif text-2xl text-primary mb-4">
              No pieces found
            </p>
            <p className="text-xs tracking-widest uppercase text-nude mb-8">
              Try adjusting your filters
            </p>
            <button onClick={clearFilters} className="btn-secondary">
              Clear Filters
            </button>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center space-x-2 mt-16">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => updateFilter('page', String(i + 1))}
                className={`w-8 h-8 text-xs tracking-wide transition-colors ${
                  page === i + 1
                    ? 'bg-primary text-white'
                    : 'border border-light text-primary hover:border-primary'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopPage;